import { useEffect, useState } from 'react';

import { makeStyles as _makeStyles, useTheme as _useTheme } from '@material-ui/core/styles';
import createTheme from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { StyleRules } from '@material-ui/styles';
import Color from 'color';
import type { Styles } from 'react-jss';

declare module '@material-ui/core/styles/createBreakpoints' {
  interface BreakpointOverrides {
    xs: true; // 移除 `xs` 断点
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true; // 添加 `xxl` 断点
  }
}

declare module '@material-ui/core/styles/createPalette' {
  interface PaletteOptions {
    base: {
      100: string;
      200: string;
      300: string;
    };
    accent: {
      main: string;
      dark: string;
      focus: string;
    };
    neutral: {
      main: string;
      content: string;
    };
    similar: {
      main: string;
    };
  }

  interface Palette {
    base: PaletteOptions['base'];
    accent: PaletteOptions['accent'];
    neutral: PaletteOptions['neutral'];
    similar: PaletteOptions['similar'];
  }
}

const defaultTheme = createTheme();
export const muiTheme = createTheme({
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2250,
    },
  },
  palette: {
    primary: {
      main: '#00647D',
      dark: '#174C59',
      light: '#CCF8FF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#62A579',
      dark: '#20A398',
      contrastText: '#FFFFFF',
    },
    info: {
      light: '#3D3D71',
      main: '#15152F',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    warning: {
      light: '#f3bf8f',
      main: '#FFB672',
      dark: '#FA6400',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#CF3F1B',
    },
    neutral: {
      main: '#000000',
      content: '#F3F3EE',
    },
    grey: {
      50: '#fbfbfb',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#999999',
      600: '#757575',
      700: '#666666',
      800: '#333333',
      900: '#181818',
    },
    base: {
      100: '#ffffff',
      200: '#fafaf8',
      300: '#e8e8da',
    },
    accent: {
      main: '#EF9E00',
      dark: '#EF8200',
      focus: '#774202',
    },
    similar: {
      main: '#004B8D',
    },
  },
  typography: {
    body1: {
      fontWeight: 200,
    },
    body2: {
      fontWeight: 200,
    },
  },
  overrides: {
    MuiInputBase: {
      root: {
        fontSize: '0.875rem',
        [defaultTheme.breakpoints.down('xs')]: {
          fontSize: '0.6875rem',
        },
      },
      input: {
        font: undefined,
      },
    },
    MuiFormControl: {
      marginNormal: {
        [defaultTheme.breakpoints.down('xs')]: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
    MuiIconButton: {
      root: {
        [defaultTheme.breakpoints.down('xs')]: {
          padding: 0,
        },
      },
    },
    MuiSnackbar: {
      anchorOriginTopCenter: {
        [defaultTheme.breakpoints.up('sm')]: {
          top: '75px',
          left: '50%',
          right: 'auto',
          transform: 'translateX(-50%)',
        },
      },
    },
  },
});

export type MuiTheme = typeof muiTheme;

export const createStyles = <T extends Styles>(styles: T) => styles;

export const baseText = {
  fontWeight: 400,
  fontFamily: '"游ゴシック体", "Yu Gothic", "YuGothic", sans-serif',
};

export const lineClamp = (lines: number) =>
  ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
  } as const);

export function makeMuiStyles<Props extends object = {}, ClassKey extends string = string>(
  callback: (
    _theme: MuiTheme,
    _commonStyles: ReturnType<typeof common>,
  ) => StyleRules<Props, ClassKey>,
) {
  return _makeStyles((t) => callback(t, common(t)));
}

const flexStyles = {
  flexCenter: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  flexColumn: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
  },
  flexColumnCenter: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  flexColumnReverse: {
    display: 'flex' as const,
    flexDirection: 'column-reverse' as const,
  },
  flexInline: {
    display: 'flex' as const,
    flexDirection: 'row' as const,
  },
  justifyStart: {
    justifyContent: 'flex-start',
  },
  justifyEnd: {
    justifyContent: 'flex-end',
  },
  justifyCenter: {
    justifyContent: 'center',
  },
  justifyBetween: {
    justifyContent: 'space-between',
  },
  justifyAround: {
    justifyContent: 'space-around',
  },
  justifyEvenly: {
    justifyContent: 'space-evenly',
  },
  itemsCenter: {
    alignItems: 'center',
  },
  itemsStart: {
    alignItems: 'flex-start',
  },
  itemsEnd: {
    alignItems: 'flex-end',
  },
  itemsStretch: {
    alignItems: 'stretch',
  },
  selfStart: {
    alignSelf: 'flex-start',
  },
  selfCenter: {
    alignSelf: 'center',
  },
  flex1: {
    flex: 1,
  },
  preEllipsis: {
    whiteSpace: 'pre',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
} as const;

export const common = (theme: MuiTheme) =>
  ({
    ...flexStyles,
    extraLargeText: {
      ...baseText,
      fontSize: '3.75rem',
    },
    largerText: {
      ...baseText,
      fontSize: '2.25rem',
    },
    largeText: {
      ...baseText,
      fontSize: '1.5rem',
    },
    biggerText: {
      ...baseText,
      fontSize: '1.25rem',
    },
    biggerTextImportant: {
      ...baseText,
      fontSize: '1.25rem !important',
    },
    bigText: {
      ...baseText,
      fontSize: '1.125rem',
    },
    normalText: {
      ...baseText,
      fontSize: '1.0rem',
      lineHeight: '1.375rem',
    },
    middleText: {
      ...baseText,
      fontSize: '0.875rem',
    },
    middleTextImportant: {
      ...baseText,
      fontSize: '0.85rem !important',
    },
    hideSm: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    smallerText: {
      ...baseText,
      fontSize: '0.75rem',
    },
    exSmallerText: {
      ...baseText,
      fontSize: '0.625rem',
    },
    titleText: {
      ...baseText,
      fontSize: '2.25rem',
    },
    pageTitleText: {
      ...baseText,
      fontWeight: 700,
      fontSize: 44,
      lineHeight: '66px',
    },
    dialogTitleText: {
      ...baseText,
      fontWeight: 700,
      fontSize: 16,
    },
    /**
     * color: #f5f5f5
     */
    textGray100: {
      color: theme.palette.grey['100'],
    },
    /**
     * color: #eeeeee
     */
    textGray200: {
      color: theme.palette.grey['200'],
    },
    /**
     * color: #e0e0e0
     */
    textGray300: {
      color: theme.palette.grey['300'],
    },
    /**
     * color: #bdbdbd
     */
    textGray400: {
      color: theme.palette.grey['400'],
    },
    /**
     * color: #999999
     */
    textGray500: {
      color: theme.palette.grey['500'],
    },
    /**
     * color: #757575
     */
    textGray600: {
      color: theme.palette.grey['600'],
    },
    /**
     * color: #666666
     */
    textGray700: {
      color: theme.palette.grey['700'],
    },
    /**
     * color: #333333
     */
    textGray800: {
      color: theme.palette.grey['800'],
    },
    /**
     * color: #181818
     */
    textGray900: {
      color: theme.palette.grey['900'],
    },
    textGray100Important: {
      color: `${theme.palette.grey['100']} !important`,
    },
    textGray200Important: {
      color: `${theme.palette.grey['200']} !important`,
    },
    textGray300Important: {
      color: `${theme.palette.grey['300']} !important`,
    },
    textGray400Important: {
      color: `${theme.palette.grey['400']} !important`,
    },
    textGray500Important: {
      color: `${theme.palette.grey['500']} !important`,
    },
    textGray600Important: {
      color: `${theme.palette.grey['600']} !important`,
    },
    textGray700Important: {
      color: `${theme.palette.grey['700']} !important`,
    },
    textGray800Important: {
      color: `${theme.palette.grey['800']} !important`,
    },
    textGray900Important: {
      color: `${theme.palette.grey['900']} !important`,
    },
    textWhite: {
      color: theme.palette.common.white,
    },
    textBlack: {
      color: theme.palette.common.black,
    },
    textPrimary: {
      color: theme.palette.primary.main,
    },
    textPrimaryImportant: {
      color: `${theme.palette.primary.main} !important`,
    },
    textPrimaryDark: {
      color: theme.palette.primary.dark,
    },
    textPrimaryDarkImportant: {
      color: `${theme.palette.primary.main} !important`,
    },
    textSecondary: {
      color: theme.palette.secondary.main,
    },
    textSecondaryImportant: {
      color: `${theme.palette.secondary.main} !important`,
    },
    textError: {
      color: theme.palette.error.main,
    },
    textErrorImportant: {
      color: `${theme.palette.error.main} !important`,
    },
    textBase100: {
      color: theme.palette.base[100],
    },
    textBase200: {
      color: theme.palette.base[200],
    },
    textBase300: {
      color: theme.palette.base[300],
    },
    textAccent: {
      color: theme.palette.accent.main,
    },
    textAccentFocus: {
      color: theme.palette.accent.focus,
    },
    fillWhiteImportant: {
      fill: `${theme.palette.common.white} !important`,
    },
    fillBlackImportant: {
      fill: `${theme.palette.common.black} !important`,
    },
    fontWeight100: {
      fontWeight: 100,
    },
    fontWeight200: {
      fontWeight: 200,
    },
    fontWeight300: {
      fontWeight: 300,
    },
    fontWeight400: {
      fontWeight: 400,
    },
    fontWeight500: {
      fontWeight: 500,
    },
    fontWeight600: {
      fontWeight: 600,
    },
    fontWeight700: {
      fontWeight: 700,
    },
    fontWeight800: {
      fontWeight: 800,
    },
    fontWeight900: {
      fontWeight: 900,
    },
    fontWeight100Important: {
      fontWeight: `100 !important` as any,
    },
    fontWeight200Important: {
      fontWeight: `200 !important` as any,
    },
    fontWeight300Important: {
      fontWeight: `300 !important` as any,
    },
    fontWeight400Important: {
      fontWeight: `400 !important` as any,
    },
    fontWeight500Important: {
      fontWeight: `500 !important` as any,
    },
    fontWeight600Important: {
      fontWeight: `600 !important` as any,
    },
    fontWeight700Important: {
      fontWeight: `700 !important` as any,
    },
    fontWeight800Important: {
      fontWeight: `800 !important` as any,
    },
    fontWeight900Important: {
      fontWeight: `900 !important` as any,
    },
    fontChina: {
      fontFamily: 'sans-serif',
    },
    multilineContainer: {
      display: 'flex' as const,
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
    },
    verticalSpace1: {
      '& > * + *': {
        marginTop: '1rem',
      },
    },
    verticalSpace2: {
      '& > * + *': {
        marginTop: '1.5rem',
      },
    },
    verticalSpace3: {
      '& > * + *': {
        marginTop: '2rem',
      },
    },
    verticalSpace4: {
      '& > * + *': {
        marginTop: '3rem',
      },
    },
    horizontalSpace0: {
      '& > * + *': {
        marginLeft: 0,
      },
    },
    horizontalSpace1: {
      '& > * + *': {
        marginLeft: '1rem',
      },
    },
    horizontalSpace2: {
      '& > * + *': {
        marginLeft: '1.5rem',
      },
    },
    horizontalSpace3: {
      '& > * + *': {
        marginLeft: '2rem',
      },
    },
    horizontalSpace4: {
      '& > * + *': {
        marginLeft: '4rem',
      },
    },
    baseButton: {
      cursor: 'pointer' as const,
      userSelect: 'none' as const,
      transition: 'color 0.3s ease',
      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
    backgroundCenter: {
      backgroundSize: 'cover' as const,
      backgroundRepeat: 'no-repeat' as const,
      backgroundPosition: 'center' as const,
    },
    absoluteFill: {
      position: 'absolute' as const,
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    link: {
      ...baseText,
      color: theme.palette.primary.main,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    linkDisabled: {
      textDecoration: 'initial !important',
      color: `${theme.palette.grey[700]} !important`,
      '&:hover': {
        cursor: 'not-allowed !important',
      },
    },
    preventEvents: {
      pointerEvents: 'none',
    },
    caution: {
      ...baseText,
      color: theme.palette.primary.main,
    },
    redirectingPlaceholder: {
      ...baseText,
      display: 'flex',
      color: theme.palette.text.primary,
      justifyContent: 'center' as const,
      marginTop: '8rem',
    },
    textAlignJustify: {
      textAlign: 'justify',
    },
    textAlignCenter: {
      textAlign: 'center',
    },
    textAlignRight: {
      textAlign: 'right',
    },
    textTrim: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    heightFill: {
      height: '100%',
    },
    floatLeft: {
      float: 'left',
    },
    floatRight: {
      float: 'right',
    },
    whitespacePre: {
      whiteSpace: 'pre',
    },
    whitespacePreWrap: {
      whiteSpace: 'pre-wrap',
    },
    wordBreakBreakAll: {
      wordBreak: 'break-all',
    },
    overflowVisible: {
      overflow: 'visible !important',
    },
    underline: {
      textDecoration: 'underline',
    },
    contentsHoverPrimary: {
      display: 'contents',
      '&:hover > *': {
        backgroundColor: Color(theme.palette.primary.main).alpha(0.05).toString(),
      },
    },
    contentsHoverTextGray700: {
      display: 'contents',
      '&:hover > *': {
        fontWeight: 700,
        color: theme.palette.grey[700],
      },
    },
    contentsHoverTextPrimary: {
      display: 'contents',
      '&:hover > *': {
        fontWeight: 700,
        color: theme.palette.primary.main,
      },
    },
    contentsHoverTextPrimaryImportant: {
      display: 'contents',
      '&:hover > *': {
        fontWeight: 700,
        color: `${theme.palette.primary.main} !important`,
      },
    },
    contentsHoverTextAccentFocus: {
      display: 'contents',
      '&:hover > *': {
        fontWeight: 700,
        color: theme.palette.accent.focus,
      },
    },
    backgroundPrimary: {
      backgroundColor: theme.palette.primary.main,
    },
    backgroundAccentFocus: {
      backgroundColor: theme.palette.accent.focus,
    },
    backgroundPrimaryAlpha3: {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.03).toString(),
    },
    backgroundPrimaryAlpha5: {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.05).toString(),
    },
    backgroundPrimaryAlpha10: {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
    backgroundPrimaryAlpha20: {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.2).toString(),
    },
    backgroundPrimaryAlpha40: {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.4).toString(),
    },
    backgroundGray50: {
      backgroundColor: theme.palette.grey['50'],
    },
    backgroundGray100: {
      backgroundColor: theme.palette.grey['100'],
    },
    backgroundGray200: {
      backgroundColor: theme.palette.grey['200'],
    },
    backgroundGray300: {
      backgroundColor: theme.palette.grey['300'],
    },
    backgroundGray400: {
      backgroundColor: theme.palette.grey['400'],
    },
    backgroundGray500: {
      backgroundColor: theme.palette.grey['500'],
    },
    backgroundGray600: {
      backgroundColor: theme.palette.grey['600'],
    },
    backgroundGray700: {
      backgroundColor: theme.palette.grey['700'],
    },
    backgroundGray800: {
      backgroundColor: theme.palette.grey['800'],
    },
    backgroundGray900: {
      backgroundColor: theme.palette.grey['900'],
    },
    backgroundError: {
      backgroundColor: theme.palette.error.main,
    },
    colSpanFull: {
      gridColumn: '1 / -1',
    },
  } as const);

export const useCommonStyles = (() => _makeStyles(common))();

export type ClassMap<Keys extends string = string> = {
  [name in Keys]?: string;
};

export const useMuiTheme = () => _useTheme() as MuiTheme;

export const usePalette = () => useMuiTheme().palette;

export const useIsMobile = () => {
  const theme = useMuiTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

export const useIsSafari = () => {
  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    setIsSafari(
      typeof window !== 'undefined' ? /Version\/[\d.]+.*Safari/.test(navigator.userAgent) : false,
    );
  }, []);
  return isSafari;
};
